import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import {
  AutoAwesome as ClassifyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Message, ClassificationResult } from '@/types/api';
import { MessageType } from '@/types/enums';
import { useManuscriptChats } from '@/hooks/useMessages';
import { useChatLabel } from '@/hooks/useChatLabel';
import { useClassifyMessageRange } from '@/hooks/useRecognition';
import MessageTimeline from '@/components/messages/MessageTimeline';
import MessageContextDialog from '@/components/messages/MessageContextDialog';
import LinkToLibraryDialog from '@/components/messages/LinkToLibraryDialog';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import { getAllMessageTypes, getMessageTypeLabel } from '@/utils/messageTypes';
import { useTranslation } from 'react-i18next';

const TimelinePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const chatLabel = useChatLabel();
  // State for filters and range
  const [chatId, setChatId] = useState<number | null>(null);
  const [startMessageId, setStartMessageId] = useState<string>('');
  const [endMessageId, setEndMessageId] = useState<string>('');
  const [filterType, setFilterType] = useState<MessageType | 'ALL'>('ALL');

  // State for messages and classifications
  const [messages, setMessages] = useState<Message[]>([]);
  const [classificationMap, setClassificationMap] = useState<Map<number, ClassificationResult>>(
    new Map()
  );

  // State for context dialog
  const [contextMessageId, setContextMessageId] = useState<number | null>(null);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);

  // State for link to library dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkMessageId, setLinkMessageId] = useState<number | null>(null);
  const [linkMessageText, setLinkMessageText] = useState<string>('');
  const [linkFileName, setLinkFileName] = useState<string>('');

  // Hooks
  const classifyMutation = useClassifyMessageRange();
  const { data: chats = [], isLoading: chatsLoading } = useManuscriptChats();

  useEffect(() => {
    if (chatId === null && chats.length > 0) {
      setChatId(chats[0].id);
    }
  }, [chatId, chats]);

  const handleLoadMessages = async () => {
    if (!chatId) {
      alert(t('timeline.loadMessages.errors.noChat'));
      return;
    }

    const start = parseInt(startMessageId, 10);
    const end = parseInt(endMessageId, 10);

    if (Number.isNaN(start) || Number.isNaN(end)) {
      alert(t('timeline.loadMessages.errors.invalidMessageIds'));
      return;
    }

    if (start >= end) {
      alert(t('timeline.loadMessages.errors.startAfterEnd'));
      return;
    }

    try {
      const limit = Math.min(end - start + 1, 1000);
      const params = new URLSearchParams({
        chat_id: String(chatId),
        start_id: String(start),
        end_id: String(end),
        limit: String(limit),
      });

      const response = await fetch(`/api/v1/messages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to fetch messages: ${errorBody}`);
      }

      const data = await response.json();
      setMessages(data);
      setClassificationMap(new Map()); // Clear classifications
    } catch (error) {
      console.error('Failed to load messages:', error);
      alert(t('timeline.loadMessages.errors.failedToLoad'));
    }
  };

  const handleClassify = async () => {
    if (!chatId) {
      alert(t('timeline.loadMessages.errors.noChat'));
      return;
    }

    const start = parseInt(startMessageId, 10);
    const end = parseInt(endMessageId, 10);

    if (Number.isNaN(start) || Number.isNaN(end)) {
      alert(t('timeline.loadMessages.errors.invalidMessageIds'));
      return;
    }

    try {
      const response = await classifyMutation.mutateAsync({
        chat_id: chatId,
        start_message_id: start,
        end_message_id: end,
      });

      // Create map of message_id -> classification
      const map = new Map<number, ClassificationResult>();
      response.forEach((classification) => {
        map.set(classification.message_id, classification);
      });

      setClassificationMap(map);
    } catch (error) {
      console.error('Failed to classify messages:', error);
    }
  };

  const handleViewContext = (messageId: number) => {
    setContextMessageId(messageId);
    setContextDialogOpen(true);
  };

  const handleCloseContextDialog = () => {
    setContextDialogOpen(false);
    setContextMessageId(null);
  };

  const handleLinkToLibrary = (messageId: number, messageText?: string, fileName?: string) => {
    setLinkMessageId(messageId);
    setLinkMessageText(messageText || '');
    setLinkFileName(fileName || '');
    setLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkMessageId(null);
    setLinkMessageText('');
    setLinkFileName('');
  };

  // Filter messages by type
  const filteredMessages = messages.filter((message) => {
    if (filterType === 'ALL') return true;

    const classification = classificationMap.get(message.message_id);
    return classification?.type === filterType;
  });

  // Statistics
  const classificationStats = Array.from(classificationMap.values()).reduce(
    (acc, classification) => {
      acc[classification.type] = (acc[classification.type] || 0) + 1;
      return acc;
    },
    {} as Record<MessageType, number>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('timeline.title')}
      </Typography>

      {/* Filters and Load Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('timeline.loadMessages.title')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={chatsLoading || chats.length === 0}>
              <InputLabel>{t('timeline.loadMessages.chatLabel')}</InputLabel>
              <Select
                value={chatId ?? ''}
                onChange={(e) => setChatId(Number(e.target.value))}
                label={t('timeline.loadMessages.chatLabel')}
                displayEmpty
              >
                {chats.map((chat) => (
                  <MenuItem key={chat.id} value={chat.id}>
                    {chatLabel(chat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('timeline.loadMessages.startMessageIdLabel')}
              type="number"
              fullWidth
              value={startMessageId}
              onChange={(e) => setStartMessageId(e.target.value)}
              placeholder="e.g., 1"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('timeline.loadMessages.endMessageIdLabel')}
              type="number"
              fullWidth
              value={endMessageId}
              onChange={(e) => setEndMessageId(e.target.value)}
              placeholder="e.g., 100"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={handleLoadMessages}
              disabled={!chatId || !startMessageId || !endMessageId || chats.length === 0}
              fullWidth
              sx={{ height: '56px' }}
            >
              {t('timeline.loadMessages.button')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Classification Section */}
      {messages.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('timeline.classification.title')}</Typography>
            <Button
              variant="contained"
              startIcon={<ClassifyIcon />}
              onClick={handleClassify}
              disabled={classifyMutation.isPending}
              color="secondary"
            >
              {classifyMutation.isPending ? t('timeline.classification.buttonClassifying') : t('timeline.classification.button')}
            </Button>
          </Box>

          {classifyMutation.isError && (
            <ErrorAlert error={classifyMutation.error} title={t('timeline.classification.errorTitle')} />
          )}

          {/* Classification Statistics */}
          {classificationMap.size > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('timeline.classification.resultsTitle', { count: classificationMap.size })}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {getAllMessageTypes().map((type) => {
                  const count = classificationStats[type] || 0;
                  if (count === 0) return null;
                  return (
                    <Chip
                      key={type}
                      label={`${getMessageTypeLabel(type)}: ${count}`}
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {/* Filter Section */}
      {messages.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('timeline.filter.title')}</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e: SelectChangeEvent) => setFilterType(e.target.value as MessageType | 'ALL')}
                  label={t('timeline.filter.title')}
                  disabled={classificationMap.size === 0}
                >
                  <MenuItem value="ALL">{t('timeline.filter.allMessages')}</MenuItem>
                  {getAllMessageTypes().map((type) => (
                    <MenuItem key={type} value={type}>
                      {getMessageTypeLabel(type)} ({classificationStats[type] || 0})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('timeline.filter.showingMessages', {
                  filtered: filteredMessages.length,
                  total: messages.length,
                })}
                {filterType !== 'ALL' && t('timeline.filter.filteredByType', { type: getMessageTypeLabel(filterType as MessageType) })}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading State */}
      {classifyMutation.isPending && (
        <LoadingSpinner message={t('timeline.loading')} />
      )}

      {/* Messages Timeline */}
      {!classifyMutation.isPending && messages.length > 0 && (
        <MessageTimeline
          messages={filteredMessages}
          classifications={classificationMap}
          onViewContext={handleViewContext}
          onLinkToLibrary={handleLinkToLibrary}
        />
      )}

      {/* Empty state */}
      {!classifyMutation.isPending && messages.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('timeline.emptyState.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('timeline.emptyState.subtitle')}
          </Typography>
        </Paper>
      )}

      {/* Context Dialog */}
      <MessageContextDialog
        open={contextDialogOpen}
        messageId={contextMessageId}
        chatId={chatId}
        onClose={handleCloseContextDialog}
      />

      {/* Link to Library Dialog */}
      <LinkToLibraryDialog
        open={linkDialogOpen}
        onClose={handleCloseLinkDialog}
        chatId={chatId}
        messageId={linkMessageId}
        messageText={linkMessageText}
        fileName={linkFileName}
        onSuccess={() => {
          // Could show a success toast here
          console.log('Successfully linked to library');
        }}
      />
    </Box>
  );
};

export default TimelinePage;
